/**
 * RequestMoreInfoOrReplyModal
 *
 * Single-step modal that handles two related flows depending on
 * `mir_pending_direction`:
 *
 *  - null / undefined  → "Request more info" (outbound to subcontractor)
 *  - "inbound"         → "Reply to info request" (replying to subcontractor)
 *
 * The caller passes the job, open/submit/submitting state and the
 * modal handles comment + optional file attachments.
 */

import {
  type JobCategoryPrefillText,
  getJobCategoryPrefillTexts,
} from '@/domains/jobs/api/jobTypesApi';
import { FileDropzone, SelectedFilesList } from '@/shared/components';
import { ATTACHMENT_FILE_TYPES } from '@/shared/lib/fileTypes';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Textarea } from '@/shared/ui/textarea';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, HelpCircle, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export interface RequestMoreInfoOrReplyJob {
  id: number;
  name: string | null;
  address: string | null;
  work_order_status: string | null;
  req_completion_date: string | null;
  mir_pending_direction: 'inbound' | 'outbound' | null | undefined;
}

interface RequestMoreInfoOrReplyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: RequestMoreInfoOrReplyJob | null;
  isSubmitting: boolean;
  onSubmit: (comment: string, files: File[]) => void | Promise<void>;
}

const formSchema = z.object({
  comments: z.string().trim().min(1, 'Required').max(500, 'Maximum 500 characters'),
});

type FormValues = z.infer<typeof formSchema>;

export function RequestMoreInfoOrReplyModal({
  open,
  onOpenChange,
  job,
  isSubmitting,
  onSubmit,
}: RequestMoreInfoOrReplyModalProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [jobCategories, setJobCategories] = useState<JobCategoryPrefillText[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');

  const selectedCategory = jobCategories.find((cat) => String(cat.id) === selectedCategoryId);

  const isReply = job?.mir_pending_direction === 'inbound';

  useEffect(() => {
    getJobCategoryPrefillTexts()
      .then(setJobCategories)
      .catch(() => setJobCategories([]));
  }, []);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { comments: '' },
  });

  const commentsValue = watch('comments');
  const hasAnyInput =
    (commentsValue?.trim().length ?? 0) > 0 || files.length > 0 || selectedCategoryId !== '';

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      if (isSubmitting) return;
      if (hasAnyInput) {
        setShowCancelConfirm(true);
      } else {
        reset();
        setFiles([]);
        onOpenChange(false);
      }
    } else {
      onOpenChange(true);
    }
  };

  const confirmCancel = () => {
    setShowCancelConfirm(false);
    reset();
    setFiles([]);
    setSelectedCategoryId('');
    onOpenChange(false);
  };

  const onFormSubmit = handleSubmit(async (values) => {
    await onSubmit(values.comments.trim(), files);
    reset();
    setFiles([]);
    setSelectedCategoryId('');
  });

  const handleFilesAccepted = (accepted: File[]) => {
    setFiles((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
      return [...prev, ...accepted.filter((f) => !seen.has(`${f.name}:${f.size}`))];
    });
  };

  const handleFileRemove = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  if (!job) return null;

  const jobMeta = [
    job.work_order_status,
    job.address,
    job.req_completion_date
      ? `Due ${new Date(job.req_completion_date).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="max-w-xl max-h-[90vh] overflow-hidden flex flex-col gap-0 p-0"
          onInteractOutside={(e) => {
            if (isSubmitting) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (isSubmitting) e.preventDefault();
          }}
        >
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 dark:bg-amber-950 flex items-center justify-center shrink-0">
                <HelpCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <DialogTitle>{isReply ? 'Reply to info request' : 'Request more info'}</DialogTitle>
                <DialogDescription className="mt-1 truncate">
                  {job.name ?? `Job #${job.id}`}
                  {jobMeta ? ` · ${jobMeta}` : ''}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <form onSubmit={onFormSubmit} className="flex-1 overflow-y-auto">
            <div className="px-6 py-5 space-y-5">
              {/* Job category */}
              <div className="space-y-2">
                <Label htmlFor="rmi-category">
                  Job category <span className="text-muted-foreground text-xs">(optional)</span>
                </Label>
                <Select value={selectedCategoryId} onValueChange={setSelectedCategoryId}>
                  <SelectTrigger id="rmi-category">
                    <SelectValue placeholder="Select a category…" />
                  </SelectTrigger>
                  <SelectContent>
                    {jobCategories.map((cat) => (
                      <SelectItem key={cat.id} value={String(cat.id)}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {selectedCategory?.prefill_text && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      const current = commentsValue?.trim() ?? '';
                      setValue(
                        'comments',
                        current
                          ? `${current}\n\n${selectedCategory.prefill_text}`
                          : selectedCategory.prefill_text,
                        { shouldValidate: true },
                      );
                    }}
                  >
                    Add prefill note
                  </Button>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="rmi-comments">
                  {isReply ? 'Your reply' : 'Information request'}{' '}
                  <span className="text-red-600">*</span>
                </Label>
                <Textarea
                  id="rmi-comments"
                  {...register('comments')}
                  placeholder={
                    isReply
                      ? 'Provide the requested information…'
                      : 'What additional information do you need?'
                  }
                  rows={6}
                  className="resize-none"
                  maxLength={500}
                  autoFocus
                />
                <div className="flex items-center justify-between text-xs">
                  <span className="text-red-600">{errors.comments?.message ?? ''}</span>
                  <span className="text-muted-foreground">{commentsValue?.length ?? 0} / 500</span>
                </div>
              </div>

              {/* Supporting documentation */}
              <div className="space-y-2">
                <Label>Supporting documentation (optional)</Label>
                <FileDropzone
                  preset={ATTACHMENT_FILE_TYPES}
                  multiple
                  disabled={isSubmitting}
                  onFilesAccepted={handleFilesAccepted}
                />
                <SelectedFilesList
                  files={files}
                  onRemove={handleFileRemove}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </form>

          <DialogFooter className="px-6 py-4 border-t border-border bg-muted/30 sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => void onFormSubmit()}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? isReply
                  ? 'Sending reply…'
                  : 'Sending…'
                : isReply
                  ? 'Send reply'
                  : 'Send request'}
            </Button>
          </DialogFooter>

          {isSubmitting && (
            <div
              className="absolute inset-0 z-20 flex items-center justify-center bg-background/75 backdrop-blur-sm"
              role="status"
              aria-busy="true"
              aria-live="polite"
            >
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-10 h-10 text-amber-600 animate-spin" />
                <p className="text-sm font-medium text-foreground">
                  {isReply ? 'Sending reply…' : 'Sending request…'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Please wait, this can take a moment.
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Discard confirm */}
      <Dialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 dark:bg-amber-950 flex items-center justify-center shrink-0">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <DialogTitle>Discard changes?</DialogTitle>
                <DialogDescription className="mt-1">
                  You have unsaved content. Closing now will lose it.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelConfirm(false)}>
              Keep editing
            </Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={confirmCancel}>
              Discard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
